import React from 'react';
import { Grid2, Paper, Typography, Button, Box } from '@mui/material';
import { styled } from '@mui/system';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useAuth } from '../AuthContext';
import StudentLearning from '../assets/StudentLearning.jpg';

const StyledCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: 16,
  textAlign: 'center',
  background: 'linear-gradient(135deg, #ffffff 0%, #f0f4f8 100%)',
  border: '1px solid rgba(173, 223, 173, 0.3)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 8px 24px rgba(76, 175, 80, 0.2)',
  },
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(3),
  },
}));

const AnimatedArrowIcon = styled(ArrowForwardIcon)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  transition: 'transform 0.3s ease-in-out',
  '.MuiButton-root:hover &': {
    animation: 'bounce 0.5s infinite',
  },
  '@keyframes bounce': {
    '0%, 100%': { transform: 'translateY(0)' },
    '50%': { transform: 'translateY(-5px)' },
  },
}));

const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.2,
      type: 'spring',
      stiffness: 100,
      damping: 15,
    },
  }),
};

const imageVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: 'easeOut',
      delay: 0.4,
    },
  },
  hover: {
    scale: 1.05,
    transition: {
      duration: 0.3,
    },
  },
};

const FeaturesSection = () => {
  const { user } = useAuth();

  const features = [
    {
      title: 'Interactive Learning',
      description: 'Engaging video lessons and interactive quizzes for Primary and Secondary students to master Maths topics with confidence.',
      icon: <VideoLibraryIcon sx={{ fontSize: 60, color: '#4caf50' }} />,
    },
    {
      title: 'Practice Worksheets',
      description: 'Downloadable worksheets for all Maths topics, perfect for Primary and Secondary students to practice and excel.',
      icon: <AssignmentIcon sx={{ fontSize: 60, color: '#4caf50' }} />,
    },
  ];

  return (
    <Box
      id="features"
      sx={{
        minHeight: '60vh',
        backgroundPosition: 'center',
        padding: { xs: 3, md: 6 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          textAlign: 'center',
          color: 'black',
          mb: 4,
          fontSize: { xs: 28, md: 36 },
        }}
      >
        Learn Maths with Maths Gem Seychelles
      </Typography>
      <Typography
        variant="subtitle1"
        sx={{
          textAlign: 'center',
          color: 'black',
          mb: 6,
          mx: 'auto',
          fontSize: { xs: 16, md: 18 },
        }}
      >
        Comprehensive resources for Primary and Secondary students, including video lessons, quizzes, and worksheets for all Maths topics.
      </Typography>

      <Grid2 container spacing={4} alignItems="center" justifyContent="center">
        {/* Cards Section */}
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Grid2 container spacing={4} justifyContent="center">
            {features.map((feature, index) => (
              <Grid2 key={index} size={{ xs: 12, sm: 6, md: 6 }}>
                <motion.div
                  custom={index}
                  variants={cardVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  <StyledCard elevation={3}>
                    <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: 'black', mb: 1 }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: '#424242', mb: 3, lineHeight: 1.6 }}
                    >
                      {feature.description}
                    </Typography>
                  </StyledCard>
                </motion.div>
              </Grid2>
            ))}
          </Grid2>
        </Grid2>

        {/* Image Section */}
        <Grid2
          size={{ xs: 12, md: 5 }}
          sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 4, md: 0 } }}
        >
          <motion.img
            src={StudentLearning}
            alt="Student learning with Maths Gem Seychelles"
            variants={imageVariants}
            initial="hidden"
            animate="visible"
            whileHover="hover"
            style={{
              width: '100%',
              maxWidth: 400,
              height: 'auto',
              borderRadius: 16,
              border: '3px solid #4caf50',
              boxShadow: '0 8px 24px rgba(76, 175, 80, 0.2)',
            }}
          />
        </Grid2>
      </Grid2>

      
    </Box>
  );
};

export default FeaturesSection;
